import { Link } from "react-router-dom";
import { StatusBadge } from "@/components/base/StatusBadge";
import { money, fmtDate, variantLabel } from "@/lib/format";
import { historicalProductName, historicalSku, historicalVariantOptions, type CatalogueHistoryFields } from "@/lib/catalogue-history";
import { FULFILLABLE_STATUSES, paymentStatusLabel, paymentStatusTone, shouldPromoteAllocateCta } from "@/lib/sales-orders";
import { buildProvenance, provenanceStoreLabel } from "@/lib/order-provenance";
import { summarizeSalesOrderItems } from "@/lib/sales-order-list-summary";
import { SalesOrderItemSummaryView } from "@/pages/sales-orders/components/SalesOrderItemSummaryView";
import { SalesOrderListAfterSalesBadge } from "@/pages/sales-orders/components/SalesOrderListAfterSalesBadge";
import type { SalesOrderListAfterSalesBadge as AfterSalesBadge } from "@/lib/sales-order-list-after-sales";

interface SalesOrderCardOrder {
  id: string;
  order_number: string;
  status: string;
  payment_status: string | null;
  order_date: string | null;
  total: number;
  source: string;
  customers?: { name: string } | null;
  locations?: { name: string } | null;
  connectors?: { name: string; provider: string | null } | null;
}

interface SalesOrderCardItem extends CatalogueHistoryFields {
  id: string;
  qty_ordered: number;
  qty_allocated?: number;
  qty_fulfilled: number;
  unit_price: number;
  is_bundle?: boolean | null;
  bundle_parent_item_id?: string | null;
  products?: { name: string; sku: string } | null;
  variants?: { sku: string | null; option_values: Record<string, string> | null } | null;
}

interface SalesOrderCardProps {
  order: SalesOrderCardOrder;
  items: SalesOrderCardItem[];
  externalOrderId: string | null;
  afterSalesBadge?: AfterSalesBadge | null;
  canFulfilRole: boolean;
  canAllocateRole: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onFulfil: () => void;
  onAllocate: () => void;
  selected?: boolean;
  onToggleSelect?: () => void;
}

const paymentBadgeCls: Record<string, string> = {
  paid: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-700",
  refunded: "bg-slate-100 text-slate-600",
  unpaid: "bg-slate-100 text-slate-600",
};

/**
 * Narrow/mobile presentation of a single sales order — same canonical helpers
 * (payment label/tone, StatusBadge, provenance, allocate-first CTA) as the
 * desktop table, no second business-state model.
 */
export default function SalesOrderCard({ order: so, items, externalOrderId, afterSalesBadge, canFulfilRole, canAllocateRole, isOpen, onToggle, onFulfil, onAllocate, selected, onToggleSelect }: SalesOrderCardProps) {
  const canFulfil = FULFILLABLE_STATUSES.includes(so.status);
  const promoteAllocate = canAllocateRole && shouldPromoteAllocateCta(so.status, items.map((it) => ({
    is_bundle: it.is_bundle,
    qty_ordered: it.qty_ordered,
    qty_allocated: it.qty_allocated ?? 0,
    qty_fulfilled: it.qty_fulfilled,
  })));
  const provenance = buildProvenance({
    source: so.source,
    connectorProvider: so.connectors?.provider ?? null,
    connectorName: so.connectors?.name ?? null,
    externalOrderId,
  });
  const tone = paymentStatusTone(so.payment_status);

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex items-start gap-2">
          {onToggleSelect && (
            <input
              type="checkbox"
              checked={Boolean(selected)}
              onChange={onToggleSelect}
              aria-label={`Select ${so.order_number}`}
              className="mt-1 w-4 h-4 rounded accent-emerald-600"
            />
          )}
          <div className="min-w-0">
          <Link to={`/sales-orders/${so.id}`} className="text-sm font-semibold text-slate-800 font-mono hover:text-emerald-700 block">
            {so.order_number}
          </Link>
          {afterSalesBadge && <SalesOrderListAfterSalesBadge badge={afterSalesBadge} />}
          <div className="mt-1">
            <SalesOrderItemSummaryView
              compact
              summary={summarizeSalesOrderItems(items.map((it) => ({
                productName: historicalProductName(it),
                sku: historicalSku(it),
                optionValues: historicalVariantOptions(it),
                qty: it.qty_ordered,
                bundleParentItemId: it.bundle_parent_item_id ?? null,
              })))}
            />
          </div>
          <span className="text-xs text-slate-500">{so.customers?.name ?? "—"}</span>
          </div>
        </div>
        <StatusBadge status={so.status} />
      </div>

      <div className="flex items-start justify-between gap-2 pt-2 border-t border-slate-100">
        <div className="min-w-0">
          <span className="block text-[11px] font-medium uppercase tracking-wide text-slate-400">Total</span>
          <span className="text-sm font-semibold text-slate-800 whitespace-nowrap">{money(so.total)}</span>
        </div>
        <div className="text-right shrink-0">
          <span className="block text-[11px] font-medium uppercase tracking-wide text-slate-400">Payment</span>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${paymentBadgeCls[tone]}`}>
            {paymentStatusLabel(so.payment_status)}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 text-xs text-slate-400">
        <span className="inline-flex items-center gap-1.5 min-w-0">
          <i className={provenance.isMarketplace ? "ri-store-2-line text-emerald-600" : "ri-file-list-3-line"} />
          <span className="truncate">{provenanceStoreLabel(provenance)}</span>
        </span>
        <span className="whitespace-nowrap">{fmtDate(so.order_date)}</span>
      </div>

      {isOpen && items.length > 0 && (
        <div className="divide-y divide-slate-100 border-t border-slate-100 pt-1">
          {items.map((it) => (
            <div key={it.id} className="py-2 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-slate-800 truncate">{historicalProductName(it)}</span>
                <span className="text-slate-600 whitespace-nowrap">{money(it.unit_price)}</span>
              </div>
              <div className="text-xs text-slate-400 font-mono mt-0.5">
                {historicalSku(it)}
                {historicalVariantOptions(it) && (variantLabel(historicalVariantOptions(it)) || historicalSku(it)) ? ` · ${variantLabel(historicalVariantOptions(it)) || historicalSku(it)}` : ""}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{it.qty_fulfilled} / {it.qty_ordered} fulfilled</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 pt-1 border-t border-slate-100">
        {promoteAllocate && (
          <button
            onClick={onAllocate}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium rounded-lg whitespace-nowrap"
          >
            <i className="ri-lock-line" />
            Allocate
          </button>
        )}
        {canFulfil && canFulfilRole && !promoteAllocate && (
          <button
            onClick={onFulfil}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium rounded-lg whitespace-nowrap"
          >
            <i className="ri-truck-line" />
            Fulfil
          </button>
        )}
        {canFulfil && canFulfilRole && promoteAllocate && (
          <button
            onClick={onFulfil}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 text-xs font-medium rounded-lg whitespace-nowrap"
          >
            Fulfil
          </button>
        )}
        <Link to={`/sales-orders/${so.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700 whitespace-nowrap">
          <i className="ri-arrow-right-line" />
          View order
        </Link>
        <button
          onClick={onToggle}
          aria-expanded={isOpen}
          className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700 whitespace-nowrap"
        >
          <i className={`ri-arrow-down-s-line transition-transform ${isOpen ? "rotate-180" : ""}`} />
          {isOpen ? "Hide lines" : items.length > 0 ? `${items.length} line${items.length === 1 ? "" : "s"}` : "No lines"}
        </button>
      </div>
    </div>
  );
}